import React, { useRef } from 'react';
import { BiogasDataModel } from 'types/types';
import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import { DataTableStyled, Placeholder } from '../StyledGrid';
import { Column, ColumnBodyOptions, ColumnEditorOptions, ColumnEvent } from 'primereact/column';
import { validatedBodyTemplateWithPlaceholder } from '../GridService';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { useDictionaryValues } from 'services/api/queries';
import { getOptionsFromDictionary, isPositiveDecimal } from 'util/utils';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { useUpdateBiogasData } from 'services/api/mutations';
import { cellNumberEditor } from '../GridCellEditors';
import { BiogasDataEnum } from 'services/EpdClient';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import styled from 'styled-components';

interface BiogasDataGridProps {
  errors: any;
  epdVersionId: string;
  biogasData: BiogasDataModel;
}

type TRow = {
  key: number;
  field: BiogasDataEnum;
  fieldName: React.ReactNode;
  value: string | number | null | undefined;
};

const BiogasDataGrid: React.FC<BiogasDataGridProps> = ({ errors, epdVersionId, biogasData }) => {
  const isReadOnly = useIsReadOnlyMode();
  const rowClassName = () => ({ 'p-disabled': isReadOnly });
  const dictionariesQuery = useDictionaryValues();
  const typeOfBiogasMixRawOptions = getOptionsFromDictionary(
    dictionariesQuery.data,
    EPD_DICTIONARY_CODES.TYPE_OF_BIOGAS_MIX
  );

  const updateBiogasMutation = useUpdateBiogasData(epdVersionId);

  const biogasTable = useRef<any>(null);

  const cellEditor = (options: ColumnEditorOptions) => {
    switch (options.rowData.field) {
      case BiogasDataEnum.TypeOfBiogasMix:
        return cellSelectEditor(options);
      case BiogasDataEnum.ClimateImpactIntensity:
        return cellNumberEditor(options);
    }
  };

  const handleProcessNameChange = (propertyName: string, value: string) => {
    if (!biogasData) return;

    const newData = {
      ...biogasData,
      [propertyName]: value,
    };

    updateBiogasMutation.mutate(newData);
  };

  const cellSelectEditor = (options: ColumnEditorOptions) => (
    <Dropdown
      style={{ width: '100%', height: '28px', minWidth: '1rem' }}
      value={options.rowData[options.field]}
      optionLabel="label"
      optionValue="value"
      options={typeOfBiogasMixRawOptions}
      onChange={(e: DropdownChangeEvent) => {
        const newData = { ...biogasData };
        newData.typeOfBiogasMix = e.value;
        updateBiogasMutation.mutate(newData);
      }}
    />
  );

  const header = (
    <div className="flex flex-wrap align-items-center gap-2">
      <HeaderLabelWithIcon>
        Biogas used in {biogasData?.usedInProcessName}
        {(errors?.climateImpactIntensity || errors?.typeOfBiogasMix) && <TooltipErrorIcon content={'Required'} />}
      </HeaderLabelWithIcon>
    </div>
  );
  const getColumnsData = (data: BiogasDataModel | undefined) => {
    let index = 0;
    const columns = [
      {
        key: index,
        field: BiogasDataEnum.TypeOfBiogasMix,
        fieldName: 'Type of biogas mix',
        value: data?.typeOfBiogasMix,
      },
    ] as TRow[];
    columns.push({
      key: ++index,
      field: BiogasDataEnum.ClimateImpactIntensity,
      fieldName: (
        <>
          GWP-GHG intensity (kg CO<sub>2</sub> eq./MJ)
        </>
      ),
      value: data?.climateImpactIntensity,
    } as TRow);

    return columns;
  };

  const valueBodyTemplate = (rowData: TRow) => {
    switch (rowData.field) {
      case BiogasDataEnum.TypeOfBiogasMix:
        const typeOfBiogasMixLabel = typeOfBiogasMixRawOptions.find(
          (x) => Number(x.value) === biogasData?.typeOfBiogasMix
        )?.label;
        return typeOfBiogasMixLabel ? typeOfBiogasMixLabel : <Placeholder>Select</Placeholder>;
      case BiogasDataEnum.ClimateImpactIntensity:
        const numberValue = rowData.value as number | undefined;
        return numberValue ? (
          <>
            {numberValue} kg CO<sub>2</sub> eq./MJ
          </>
        ) : (
          <Placeholder>
            Type value, kg CO<sub>2</sub> eq./MJ
          </Placeholder>
        );
      default:
        const stringValue = rowData.value as string | undefined;
        return stringValue && stringValue?.length > 0 ? stringValue : <Placeholder>Type value</Placeholder>;
    }
  };

  const onCellEditComplete = (e: ColumnEvent) => {
    if (!biogasData) {
      return;
    }

    const { rowData, newValue, originalEvent: event } = e;

    switch (rowData.field) {
      case BiogasDataEnum.ClimateImpactIntensity:
        if (isPositiveDecimal(newValue)) {
          const newData = {
            ...biogasData,
            climateImpactIntensity: newValue,
          };

          updateBiogasMutation.mutate(newData);
        } else {
          event.preventDefault();
        }

        break;
    }
  };

  const cellTemplate = (value: any, options: ColumnBodyOptions) => {
    return validatedBodyTemplateWithPlaceholder((value as any)?.[options?.field]);
  };

  return (
    <>
      {biogasData?.ord != 1 && (
        <div style={{ marginBottom: 16 }}>
          <WizardTextInputComponent
            name="usedInProcessName"
            value={biogasData?.usedInProcessName ?? ''}
            type="text"
            label="Biogas used in"
            placeholder="Unnamed process"
            maxLength={256}
            required
            onChanged={handleProcessNameChange}
            error={errors ? errors.usedInProcessName : null}
          />
        </div>
      )}
      <DataTableStyled
        header={header}
        value={getColumnsData(biogasData)}
        dataKey="key"
        showGridlines
        editMode="cell"
        rowGroupMode="rowspan"
        groupRowsBy="fieldName"
        ref={biogasTable}
        rowClassName={rowClassName}
      >
        <Column field="fieldName" headerStyle={{ display: 'none' }} style={{ verticalAlign: 'top' }} body={cellTemplate} />
        <Column
          field="value"
          body={valueBodyTemplate}
          editor={isReadOnly ? undefined : cellEditor}
          onCellEditComplete={onCellEditComplete}
          headerStyle={{ display: 'none' }}
          style={{ width: '75%' }}
        />
      </DataTableStyled>
    </>
  );
};

const HeaderLabelWithIcon = styled(HeaderCaptionSemiBold)`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

export default BiogasDataGrid;
